import { AxiosInstance, AxiosResponse } from 'axios';
import {
  CommonObjectInterface,
  JSHelpersInterface,
  StateInterface,
} from './interfaces';

class JSHelpers implements JSHelpersInterface {
  private axios: AxiosInstance;

  constructor(axios: AxiosInstance) {
    this.axios = axios;
  }

  public parseInt(
    value: string | number,
    defaultValue: number | null = 0
  ): number | null {
    if (typeof value === 'number') {
      return Math.round(value);
    }
    if (typeof defaultValue !== 'number' && defaultValue !== null) {
      throw new Error('Значение "defaultValue" должно иметь тип: number|null.');
    }
    if (typeof value !== 'string') {
      return defaultValue;
    }

    const intValue = parseInt(this.normalizeNumberString(value), 10);
    if (isNaN(intValue)) {
      return defaultValue;
    }
    return intValue;
  }

  public parseFloat(
    value: string | number,
    defaultValue: number = 0.0
  ): number | null {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof defaultValue !== 'number' && defaultValue !== null) {
      throw new Error('Значение "defaultValue" должно иметь тип: number|null.');
    }
    if (typeof value !== 'string') {
      return defaultValue;
    }

    const floatValue = parseFloat(this.normalizeNumberString(value));
    if (isNaN(floatValue)) {
      return defaultValue;
    }
    return floatValue;
  }

  public getNetFromTax(
    tax: string | number,
    taxRate: string | number
  ): number | false {
    if (typeof tax !== 'string' && typeof tax !== 'number') {
      throw new Error('Значение "tax" должно иметь тип: number|null');
    }
    if (typeof taxRate !== 'string' && typeof taxRate !== 'number') {
      throw new Error('Значение "taxRate" должно иметь тип: number|null');
    }
    const taxValue = (this.parseFloat(tax) ?? 0.0) * 100;
    const taxRateValue = this.parseFloat(taxRate) ?? 0;
    if (taxRateValue === 0) {
      return false;
    }
    const netValue = (taxValue * 100) / taxRateValue;

    return Math.round(netValue) / 100;
  }

  public getTaxFromNet(net: string | number, taxRate: string | number): number {
    if (typeof net !== 'string' && typeof net !== 'number') {
      throw new Error('Значение "net" должно иметь тип: number|null');
    }
    if (typeof taxRate !== 'string' && typeof taxRate !== 'number') {
      throw new Error('Значение "taxRate" должно иметь тип: number|null');
    }
    const netValue = (this.parseFloat(net) ?? 0.0) * 100;
    const taxRateValue = this.parseFloat(taxRate) ?? 0.0;
    const taxValue = (netValue * taxRateValue) / 100;

    return Math.round(taxValue) / 100;
  }

  public getTaxFromTotal(
    total: string | number,
    taxRate: string | number
  ): number {
    if (typeof total !== 'string' && typeof total !== 'number') {
      throw new Error('Значение "total" должно иметь тип: number|null');
    }
    if (typeof taxRate !== 'string' && typeof taxRate !== 'number') {
      throw new Error('Значение "taxRate" должно иметь тип: number|null');
    }
    const totalValue = (this.parseFloat(total) ?? 0.0) * 100;
    const taxRateValue = this.parseFloat(taxRate) ?? 0.0;
    const taxValue = totalValue - totalValue / (1 + taxRateValue / 100);

    return Math.round(taxValue) / 100;
  }

  public getTaxRateFromNetAndTax(
    net: string | number,
    tax: string | number
  ): number {
    if (typeof net !== 'string' && typeof net !== 'number') {
      throw new Error('Значение "net" должно иметь тип: number|null');
    }
    if (typeof tax !== 'string' && typeof tax !== 'number') {
      throw new Error('Значение "tax" должно иметь тип: number|null');
    }
    const netValue = this.parseFloat(net) ?? 0.0;
    const taxValue = this.parseFloat(tax) ?? 0.0;
    if (net === 0) {
      return 0;
    }
    const taxRate = taxValue / (netValue / 100);

    return Math.round(taxRate);
  }

  public isJsonString(json: string): boolean {
    if (typeof json !== 'string') {
      throw new Error('Значение "json" должно иметь тип: string');
    }
    try {
      JSON.parse(json);
    } catch (e) {
      return false;
    }
    return true;
  }

  public isEmpty(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    if (typeof value === 'string' && value === '') {
      return true;
    }
    if (typeof value === 'number' && (value === 0 || value === 0.0)) {
      return true;
    }
    if (typeof value === 'boolean' && value === false) {
      return true;
    }
    if (typeof value === 'object') {
      if (Array.isArray(value) && value.length === 0) {
        return true;
      }
      if (!Array.isArray(value) && Object.keys(value).length === 0) {
        return true;
      }
    }
    return false;
  }

  public sortArrayOfObjectsByProperty(
    data: Array<{ [key: string]: string }>,
    property: string
  ): Array<{ [key: string]: string }> {
    if (!Array.isArray(data)) {
      throw new Error('Значение "data" должно иметь тип: array');
    }
    if (typeof property !== 'string') {
      throw new Error('Значение "property" должно иметь тип: string');
    }
    data.sort((a, b) => {
      const fa = a[property].toLowerCase();
      const fb = b[property].toLowerCase();

      if (fa < fb) {
        return -1;
      }
      if (fa > fb) {
        return 1;
      }
      return 0;
    });
    return data;
  }

  public round(value: number, accuracy: number = 0): number {
    if (typeof value !== 'number') {
      throw new Error(
        'Параметр "value" обязателен и должен иметь тип: number.'
      );
    }
    if (typeof accuracy !== 'number') {
      throw new Error(
        'Параметр "accuracy" обязателен и должен иметь тип: number.'
      );
    }
    if (accuracy < 0) {
      throw new Error('Параметр accuracy не может быть отрицательным.');
    }
    const num = Math.pow(10, accuracy);
    return Math.round(value * num) / num;
  }

  public addParamsToUrl(
    url: string,
    params: { [key: string]: string } = {}
  ): string {
    if (typeof url !== 'string' || this.isEmpty(url)) {
      throw new Error('Параметр "url" обязателен и должен иметь тип: string.');
    }
    if (
      typeof params !== 'object' &&
      params === null &&
      !Array.isArray(params)
    ) {
      throw new Error(
        'Параметр "params" обязателен и должен иметь тип: object и формат {param: value}.'
      );
    }
    let newUrl = url;
    let prependArray: Array<string> = [];
    if (url.indexOf('?') !== -1) {
      const urlParts = newUrl.split('?');
      if (urlParts.length > 1 && urlParts[1] !== '') {
        prependArray = urlParts[1].split('&');
      }
      newUrl = urlParts[0];
    }
    let paramsArray = Object.keys(params).map(
      (param) => `${param}=${params[param]}`
    );
    const resultParamsArray = prependArray.concat(paramsArray);

    return `${newUrl}?` + resultParamsArray.join('&');
  }

  ajaxGet(url: string): Promise<AxiosResponse> {
    if (typeof url !== 'string' || url === '') {
      throw new Error('Параметр "url" обязателен и должен иметь тип: string.');
    }
    return this.axios({
      method: 'get',
      url: url,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
  }

  ajaxPost(url: string, data: { [key: string]: string } = {}, csrf = '') {
    if (typeof url !== 'string' || this.isEmpty(url)) {
      throw new Error('Параметр "url" обязателен и должен иметь тип: string.');
    }
    if (csrf === '') {
      const element: HTMLMetaElement | null = document.querySelector(
        'meta[name="csrf-token"]'
      );
      if (element) {
        csrf = element.content;
      }
    }
    return this.axios({
      method: 'post',
      url: url,
      data: data,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrf,
      },
    });
  }

  propertyExists(obj: { [key: string]: any }, propName: string): boolean {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
      throw new Error('Параметр "obj" обязателен и должен иметь тип: object.');
    }
    if (typeof propName !== 'string' || this.isEmpty(propName)) {
      throw new Error(
        'Параметр "propName" обязателен и должен иметь тип: string.'
      );
    }
    return Object.prototype.hasOwnProperty.call(obj, propName);
  }

  setPropertyByPath(
    obj: { [key: string]: string | { [key: string]: string } },
    path: string | Array<string>,
    value: any
  ): void {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
      throw new Error('Параметр "obj" обязателен и должен иметь тип: object.');
    }
    if (typeof path !== 'string' && !Array.isArray(path)) {
      throw new Error(
        'Параметр "path" обязателен и должен иметь тип: string|object.'
      );
    }
    if (this.isEmpty(path)) {
      throw new Error('Параметр "path" не может быть пустым.');
    }
    let levels: Array<string> = [];

    if (Array.isArray(path)) {
      levels = path;
    } else if (typeof path === 'string') {
      if (path.indexOf('[') !== -1) {
        levels = path
          .split('[')
          .map((value) => value.replace(']', ''))
          .filter((value) => value);
      } else if (path.indexOf('.') !== -1) {
        levels = path.split('.');
      } else {
        throw new Error(
          'Параметр path должен иметь формат "a.b.c" или "a[b][c].'
        );
      }
    }
    this.writePropValueToObject(obj, levels, value);
  }

  diff(a: Array<string>, b: Array<string>): Array<string> {
    if (typeof a !== 'object' || !Array.isArray(a)) {
      throw new Error('Параметр "a" обязателен и должен иметь тип: array.');
    }
    if (typeof b !== 'object' || !Array.isArray(b)) {
      throw new Error('Параметр "b" обязателен и должен иметь тип: array.');
    }
    return a.filter((item) => !b.includes(item));
  }

  createState(obj: CommonObjectInterface): StateInterface {
    const helpers = this;
    const data: CommonObjectInterface = {};
    Object.keys(obj).forEach((key) => {
      data[key] = obj[key];
    });
    return {
      data,
      get(key: string): any {
        if (!helpers.propertyExists(this.data, key)) {
          throw new Error(`Unknown property key '${key}'`);
        }
        return this.data[key] ?? null;
      },
      set(key: string, value: any): void {
        if (!helpers.propertyExists(this.data, key)) {
          throw new Error(`Unknown property key '${key}'`);
        }
        this.data[key] = value;
      },
    };
  }

  moneyFormat(value: number | string): string {
    const money = !this.isEmpty(value)
      ? typeof value === 'string'
        ? (this.parseFloat(value, 0) ?? 0)
        : value
      : 0;

    const formatter = new Intl.NumberFormat('ru-RU', {
      style: 'decimal',
      maximumFractionDigits: 2,
    });

    return formatter.format(money);
  }

  private writePropValueToObject(
    child: { [key: string]: string | { [key: string]: string } },
    levels: Array<string>,
    value: any
  ): void {
    const name = levels.shift();
    if (name) {
      if (levels.length === 0) {
        child[name] = value;
      } else {
        const propValue = {};
        if (!this.propertyExists(child, name)) {
          child[name] = propValue;
        } else {
          if (typeof child[name] !== 'object') {
            child[name] = propValue;
          } else {
            Object.assign(propValue, child[name]);
            child[name] = propValue;
          }
        }
        this.writePropValueToObject(propValue, levels, value);
      }
    }
  }

  private normalizeNumberString(value: string): string {
    if (typeof value !== 'string') {
      throw new Error(
        'Параметр "value" обязателен и должен иметь тип: string.'
      );
    }
    return value.replace(' ', '').replace(',', '.');
  }
}

export const createC2mHelpers = (axios: AxiosInstance): JSHelpersInterface => {
  return new JSHelpers(axios);
};
